import { AbstractDestroyable, assert, Destroyable, Logger } from '@deqo/foundation';
import { tryAnd } from '../../../CodesThatsGottaGoSomewhereElse';
import { Point } from '../../../platform/geom/Point';
import { UniversalSurfaceRenderer } from '../../../platform/UniversalSurfaceRenderer';

const errl = Logger.get('app.errors');

export interface OverlayHandle extends Destroyable {

    readonly id: string;

    invalidate(relayout?: boolean, forceImmediateRefresh?: boolean): void;
}

export interface OverlayPointerTarget {
    readonly id: string;
    readonly type: string;
    test(vpt: Point): boolean;
}

export type OverlayLayout = (deltaTime: number) => OverlayPointerTarget[] | void;
export type OverlayPaint = (gfx: UniversalSurfaceRenderer) => boolean | void;

export class Overlay extends AbstractDestroyable implements OverlayHandle {

    private canvas = null as HTMLCanvasElement | null;
    private renderer = null as UniversalSurfaceRenderer | null;
    private targets = [] as OverlayPointerTarget[];
    private lastLayout = 0;
    private layoutRequested = false;
    private paintRequested = false;
    private startVisible = true;

    constructor(
        public readonly id: string,
        private readonly layout: OverlayLayout,
        private readonly paint: OverlayPaint,
    ) {
        super();
    }

    // TODO: Come back and change this
    public get visible() {
        return this.canvas?.style?.opacity === '1';
    }

    public set visible(value: boolean) {
        this.setCanvasVisibility(value);
        this.startVisible = value;
    }

    public connect(to: HTMLElement): void {
        assert(!this.canvas, 'Overlay: cannot connect when already connected?');
        assert(to && to instanceof HTMLCanvasElement, 'Overlay: connect argument must be a <canvas> element.');
        this.canvas = to;
        this.setCanvasVisibility(this.startVisible);
        this.renderer = UniversalSurfaceRenderer.from(this.canvas);
        this.refresh();
    }

    public disconnect(): void {
        this.canvas = null;
        this.renderer = null;
    }

    public test(vpt: Point) {
        return this.targets.filter(x => x.test(vpt));
    }

    public invalidate(relayout: boolean = false, forceImmediateRefresh: boolean = false): boolean {
        if (!this.canvas || !this.renderer || !!this.destroyed()) return false;
        if (!this.paintRequested) {
            this.paintRequested = true;
            this.layoutRequested = relayout;
            if (forceImmediateRefresh) {
                this.refresh();
            }
            else {
                requestAnimationFrame(this.refresh);
            }
        }
        return true;
    }

    private refresh = () => {
        const { canvas, renderer } = this;
        if (!canvas || !renderer) {
            errl.warn('paint() called when not connected?');
            return;
        }

        if (this.layoutRequested) {
            this.layoutRequested = false;
            const p = this.lastLayout;
            const n = performance.now();
            const dt = p === 0 ? 0 : (n - p) / 1000;
            this.targets = tryAnd(() => this.layout(dt) || [], e => errl.log(e)) || [];
            this.lastLayout = n;
        }

        renderer.reset(true);

        this.paintRequested = false;
        tryAnd(() => this.paint(this.renderer), e => errl.log(e));
        this.paint(this.renderer);
    };

    private setCanvasVisibility(value: boolean) {
        if (this.canvas) {
            this.canvas.style.transition = 'opacity 150ms ease-out';
            this.canvas.style.opacity = value ? '1' : '0';
        }
    }
}
